// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Bot, Boxes, CheckCircle2, Clipboard, Code2, Download,
  FileText, Package, Play, Plug, Save, Sparkles, Wrench, Workflow,
} from 'lucide-react';

type CapabilityKind = 'skill' | 'plugin' | 'mini_agent' | 'template' | 'app_workflow';
type ExportTarget = 'agentsanywhere' | 'claude' | 'codex' | 'codex_plugin';

const KIND_META: Record<CapabilityKind, {
  label: string;
  icon: React.ElementType;
  description: string;
  namePlaceholder: string;
}> = {
  skill: {
    label: 'Skill',
    icon: Wrench,
    description: 'Reusable instructions, examples, and resources for repeatable work.',
    namePlaceholder: 'Branded proposal writer',
  },
  plugin: {
    label: 'Plugin / app pack',
    icon: Package,
    description: 'A bundle of connectors, skills, templates, tools, and permissions.',
    namePlaceholder: 'Sales operations pack',
  },
  mini_agent: {
    label: 'Mini-agent',
    icon: Bot,
    description: 'A durable helper that runs on demand, on a schedule, or from an app trigger.',
    namePlaceholder: 'Daily Slack digest',
  },
  template: {
    label: 'Template',
    icon: FileText,
    description: 'A reusable document, slide, sheet, PDF, or report structure.',
    namePlaceholder: 'Client status deck',
  },
  app_workflow: {
    label: 'App workflow',
    icon: Workflow,
    description: 'A practical workflow from a connected app that can stay one-off or mature into a mini-agent.',
    namePlaceholder: 'Gmail follow-up tracker',
  },
};

const EXPORT_TARGETS: Array<{ id: ExportTarget; label: string; body: string }> = [
  { id: 'agentsanywhere', label: 'AgentsAnywhere', body: 'Run inside aibot/workspace as an internal capability.' },
  { id: 'claude', label: 'Claude Skill', body: 'Portable SKILL.md package for Claude surfaces.' },
  { id: 'codex', label: 'Codex Skill', body: 'Local/team skill folder for Codex.' },
  { id: 'codex_plugin', label: 'Codex Plugin', body: 'Installable distribution package with skills and app integrations.' },
];

const INPUT_CLASS = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-brand-400 focus:ring-2 focus:ring-brand-500/15';

function slugify(value: string): string {
  return String(value || 'new-capability')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'new-capability';
}

function readKind(search: string): CapabilityKind {
  const raw = new URLSearchParams(search).get('type') || 'skill';
  if (raw === 'plugin' || raw === 'mini_agent' || raw === 'template' || raw === 'app_workflow' || raw === 'skill') return raw;
  if (raw === 'one_off') return 'app_workflow';
  return 'skill';
}

function buildSkillMd(form: any): string {
  const slug = slugify(form.name);
  return `---
name: ${slug}
description: ${form.description || `Use this when the user needs ${form.name || 'this workflow'}.`}
---

# ${form.name || 'New capability'}

## Purpose
${form.description || 'Describe what this capability helps the user do.'}

## When to use
${form.useCases || '- Use when this repeatable workflow is requested.'}

## Inputs
${form.inputs || '- Source materials, user goal, connected app context, and examples.'}

## Procedure
${form.instructions || '1. Clarify the goal.\n2. Gather the required inputs.\n3. Produce the output in the approved format.\n4. Ask for review before external side effects.'}

## Output
${form.output || '- Return a polished artifact or concise summary with source links where relevant.'}

## Examples
${form.examples || '- Example request: Create this using the approved company format.'}
`;
}

function buildAgentsAnywhereJson(form: any): string {
  return JSON.stringify({
    schema: 'agentsanywhere.capability.v1',
    kind: form.kind,
    name: form.name,
    slug: slugify(form.name),
    owner: form.owner,
    description: form.description,
    connected_apps: form.connectedApps.split(',').map((s: string) => s.trim()).filter(Boolean),
    trigger: form.trigger,
    schedule: form.schedule,
    permissions: form.permissions,
    instructions: form.instructions,
    inputs: form.inputs,
    output: form.output,
    examples: form.examples,
    export_targets: form.targets,
  }, null, 2);
}

function buildPluginManifest(form: any): string {
  return JSON.stringify({
    schema_version: '1.0',
    name: slugify(form.name),
    display_name: form.name || 'New plugin',
    description: form.description || '',
    version: '0.1.0',
    skills: form.kind === 'plugin' ? [`skills/${slugify(form.name)}/SKILL.md`] : [],
    apps: form.connectedApps.split(',').map((s: string) => s.trim()).filter(Boolean),
    permissions: form.permissions.split('\n').map((s: string) => s.trim()).filter(Boolean),
  }, null, 2);
}

const CapabilityStudioPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialKind = readKind(location.search);
  const params = new URLSearchParams(location.search);
  const initialProvider = params.get('provider') || '';
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [form, setForm] = useState({
    kind: initialKind,
    name: '',
    owner: 'team',
    description: '',
    useCases: '',
    connectedApps: initialProvider,
    trigger: '',
    schedule: '',
    permissions: 'Needs approval before sending external messages or changing app data.',
    inputs: '',
    instructions: '',
    output: '',
    examples: '',
    testPrompt: '',
    targets: ['agentsanywhere', initialKind === 'plugin' ? 'codex_plugin' : 'claude', 'codex'] as ExportTarget[],
  });

  const meta = KIND_META[form.kind as CapabilityKind];
  const Icon = meta.icon;
  const skillMd = useMemo(() => buildSkillMd(form), [form]);
  const internalJson = useMemo(() => buildAgentsAnywhereJson(form), [form]);
  const pluginManifest = useMemo(() => buildPluginManifest(form), [form]);

  const setField = (key: string, value: any) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleTarget = (target: ExportTarget) => {
    setSaved(false);
    setForm((prev) => {
      const exists = prev.targets.includes(target);
      return {
        ...prev,
        targets: exists ? prev.targets.filter((t) => t !== target) : [...prev.targets, target],
      };
    });
  };

  const saveDraft = useCallback(() => {
    const key = 'aa:capability-drafts:v1';
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const draft = {
      id: `${Date.now()}`,
      updatedAt: new Date().toISOString(),
      ...form,
      slug: slugify(form.name),
    };
    localStorage.setItem(key, JSON.stringify([draft, ...existing].slice(0, 25)));
    setSaved(true);
  }, [form]);

  const copyText = async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(id);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  const scopeWithOrbit = () => {
    window.dispatchEvent(new CustomEvent('orbit:open-with-prompt', {
      detail: {
        text: `Help me finish this ${meta.label}: ${form.name || meta.namePlaceholder}. Ask only for missing fields, then prepare it for Capability Studio.`,
        route: 'problem',
        context: 'problem',
        fresh: true,
        source: 'capability_studio',
        workRoute: form.kind,
        provider: form.connectedApps || null,
      },
    }));
  };

  return (
    <div className="space-y-5 font-sans text-gray-900">
      <header className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <button
              type="button"
              onClick={() => navigate('/my-tools')}
              className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.09em] text-brand-600">Capability Studio</div>
              <h1 className="mt-1 text-[25px] font-semibold tracking-tight text-gray-950">Create a portable capability.</h1>
              <p className="mt-1 max-w-[760px] text-[13.5px] leading-relaxed text-gray-500">
                Build once as an AgentsAnywhere capability, then export the same intent as a Claude skill, Codex skill, or Codex plugin package when appropriate.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={scopeWithOrbit}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-[12.5px] font-semibold text-gray-700 hover:border-brand-200 hover:text-brand-700 whitespace-nowrap"
            >
              <Sparkles className="h-4 w-4" />
              Scope with Orbit
            </button>
            <button
              type="button"
              onClick={saveDraft}
              className="inline-flex items-center gap-1.5 rounded-xl bg-brand-500 px-3.5 py-2 text-[12.5px] font-semibold text-white hover:bg-brand-600 whitespace-nowrap"
            >
              {saved ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {saved ? 'Draft saved' : 'Save draft'}
            </button>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <main className="space-y-5">
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              {(Object.keys(KIND_META) as CapabilityKind[]).map((kind) => {
                const M = KIND_META[kind];
                const KIcon = M.icon;
                const active = form.kind === kind;
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setField('kind', kind)}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      active ? 'border-brand-200 bg-brand-50 text-brand-800' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <KIcon className={`mb-2 h-4 w-4 ${active ? 'text-brand-600' : 'text-gray-400'}`} />
                    <div className="text-[12.5px] font-semibold">{M.label}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 rounded-xl bg-gray-50 p-3 text-[12.5px] leading-snug text-gray-500">
              <span className="font-semibold text-gray-800">{meta.label}:</span> {meta.description}
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <SectionTitle icon={Boxes} title="Definition" body="Name the artifact and define the job it performs." />
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Name">
                <input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder={meta.namePlaceholder} className={INPUT_CLASS} />
              </Field>
              <Field label="Owner">
                <select value={form.owner} onChange={(e) => setField('owner', e.target.value)} className={INPUT_CLASS}>
                  <option value="team">Team</option>
                  <option value="personal">Personal</option>
                  <option value="organization">Organization</option>
                  <option value="agentsanywhere">AgentsAnywhere managed</option>
                </select>
              </Field>
              <Field label="Connected apps" hint="Comma separated. Example: Slack, Google Drive.">
                <input value={form.connectedApps} onChange={(e) => setField('connectedApps', e.target.value)} placeholder="Slack, Google Drive" className={INPUT_CLASS} />
              </Field>
              <Field label="Trigger">
                <input value={form.trigger} onChange={(e) => setField('trigger', e.target.value)} placeholder="Manual, schedule, Slack mention, new file..." className={INPUT_CLASS} />
              </Field>
              <Field label="Schedule">
                <input value={form.schedule} onChange={(e) => setField('schedule', e.target.value)} placeholder="Daily 9am, weekly, on demand..." className={INPUT_CLASS} />
              </Field>
              <Field label="Test prompt">
                <input value={form.testPrompt} onChange={(e) => setField('testPrompt', e.target.value)} placeholder="Run this for the #sales channel..." className={INPUT_CLASS} />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Description">
                <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} rows={3} placeholder="What this capability does and when someone should use it." className={`${INPUT_CLASS} min-h-[84px]`} />
              </Field>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <SectionTitle icon={Code2} title="Instructions" body="This becomes the core skill/plugin behavior across our runtime, Claude, and Codex exports." />
            <div className="mt-4 grid grid-cols-1 gap-4">
              <Field label="Use cases">
                <textarea value={form.useCases} onChange={(e) => setField('useCases', e.target.value)} rows={3} placeholder="- Use when a manager needs a channel digest..." className={`${INPUT_CLASS} min-h-[84px]`} />
              </Field>
              <Field label="Inputs">
                <textarea value={form.inputs} onChange={(e) => setField('inputs', e.target.value)} rows={3} placeholder="- Channel names\n- Date range\n- Decision criteria" className={`${INPUT_CLASS} min-h-[84px]`} />
              </Field>
              <Field label="Procedure">
                <textarea value={form.instructions} onChange={(e) => setField('instructions', e.target.value)} rows={6} placeholder="1. Read the connected app context.\n2. Pull source messages/files.\n3. Identify decisions, blockers, and owner changes.\n4. Produce the approved output." className={`${INPUT_CLASS} min-h-[140px]`} />
              </Field>
              <Field label="Output format">
                <textarea value={form.output} onChange={(e) => setField('output', e.target.value)} rows={3} placeholder="- Executive summary\n- Decisions\n- Blockers\n- Links to sources" className={`${INPUT_CLASS} min-h-[84px]`} />
              </Field>
              <Field label="Examples">
                <textarea value={form.examples} onChange={(e) => setField('examples', e.target.value)} rows={4} placeholder="Add example prompts and expected outputs." className={`${INPUT_CLASS} min-h-[112px]`} />
              </Field>
              <Field label="Permissions and policy">
                <textarea value={form.permissions} onChange={(e) => setField('permissions', e.target.value)} rows={3} className={`${INPUT_CLASS} min-h-[84px]`} />
              </Field>
            </div>
          </section>
        </main>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <SectionTitle icon={Download} title="Export targets" body="Choose where this capability should be installable." />
            <div className="mt-4 space-y-2">
              {EXPORT_TARGETS.map((target) => {
                const checked = form.targets.includes(target.id);
                return (
                  <button
                    key={target.id}
                    type="button"
                    onClick={() => toggleTarget(target.id)}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                      checked ? 'border-brand-200 bg-brand-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md border ${
                      checked ? 'border-brand-500 bg-brand-500 text-white' : 'border-gray-300 bg-white text-transparent'
                    }`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </span>
                    <span>
                      <span className="block text-[12.5px] font-semibold text-gray-950">{target.label}</span>
                      <span className="mt-0.5 block text-[11.5px] leading-snug text-gray-500">{target.body}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <PreviewCard
            id="internal"
            title="AgentsAnywhere package"
            icon={Boxes}
            value={internalJson}
            copied={copied}
            onCopy={copyText}
          />
          <PreviewCard
            id="skillmd"
            title="SKILL.md"
            icon={Wrench}
            value={skillMd}
            copied={copied}
            onCopy={copyText}
          />
          {form.targets.includes('codex_plugin') && (
            <PreviewCard
              id="plugin"
              title="Codex plugin manifest"
              icon={Package}
              value={pluginManifest}
              copied={copied}
              onCopy={copyText}
            />
          )}

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <SectionTitle icon={Play} title="Next step" body="Use Orbit only when fields are missing. Use Workspace when this needs implementation or packaging." />
            <div className="mt-4 grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={scopeWithOrbit}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-gray-700 hover:border-brand-200 hover:text-brand-700 whitespace-nowrap"
              >
                <Sparkles className="h-4 w-4" />
                Fill gaps with Orbit
              </button>
              <Link
                to="/agents"
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-gray-950 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-gray-800 whitespace-nowrap"
              >
                <Plug className="h-4 w-4" />
                Send to Workspace
              </Link>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

function SectionTitle({ icon: Icon, title, body }: { icon: React.ElementType; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
        <Icon className="h-4 w-4" />
      </span>
      <span>
        <span className="block text-[14px] font-semibold text-gray-950">{title}</span>
        <span className="mt-0.5 block text-[12.5px] leading-snug text-gray-500">{body}</span>
      </span>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12px] font-semibold text-gray-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-gray-400">{hint}</span>}
    </label>
  );
}

function PreviewCard({ id, title, icon: Icon, value, copied, onCopy }: {
  id: string;
  title: string;
  icon: React.ElementType;
  value: string;
  copied: string | null;
  onCopy: (id: string, value: string) => void;
}) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-brand-600" />
          <span className="text-[13px] font-semibold text-gray-950">{title}</span>
        </div>
        <button
          type="button"
          onClick={() => onCopy(id, value)}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-[11.5px] font-semibold text-gray-600 hover:bg-gray-50 whitespace-nowrap"
        >
          <Clipboard className="h-3.5 w-3.5" />
          {copied === id ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap p-4 text-[11px] leading-relaxed text-gray-600">
        {value}
      </pre>
    </section>
  );
}

export default CapabilityStudioPage;
